import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Currency = 'EUR' | 'USD' | 'TRY';

interface ExchangeRates {
  USD: number;
  TRY: number;
}

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  exchangeRates: ExchangeRates;
  convertPrice: (priceInEur: number) => number;
  formatPrice: (priceInEur: number) => string;
  currencySymbol: string;
  isLoading: boolean;
  // Legacy support
  exchangeRate: number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Default rates - updated periodically, good enough for initial render
const DEFAULT_RATES: ExchangeRates = {
  USD: 1.08,
  TRY: 38.5
};

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    // SSR-safe localStorage access
    if (typeof window === 'undefined') return 'EUR';
    try {
      const saved = localStorage.getItem('selectedCurrency');
      return (saved as Currency) || 'EUR';
    } catch {
      return 'EUR';
    }
  });
  
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(() => {
    // Try to get cached rates synchronously to avoid any async during render
    if (typeof window === 'undefined') return DEFAULT_RATES;
    try {
      const cached = localStorage.getItem('exchangeRates');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_RATES;
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Fetch exchange rates ONLY after page is fully interactive (post-LCP)
  // Using a much longer delay to ensure it never appears in critical path
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        if (response.ok) {
          const data = await response.json();
          const rates = {
            USD: data.rates.USD,
            TRY: data.rates.TRY
          };
          setExchangeRates(rates);
          localStorage.setItem('exchangeRates', JSON.stringify(rates));
          localStorage.setItem('exchangeRateTimestamp', Date.now().toString());
        }
      } catch (error) {
        // Silently fail - we have good defaults
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we need to refresh (once per hour)
    const lastFetch = localStorage.getItem('exchangeRateTimestamp');
    const shouldRefresh = !lastFetch || (Date.now() - parseInt(lastFetch)) > 3600000;
    
    if (!shouldRefresh) return; // Don't fetch if cache is fresh

    // CRITICAL: Delay fetch significantly to ensure it's NEVER in critical path
    // PageSpeed measures critical path - we want this to fire well after LCP
    const delayMs = 8000; // 8 seconds - well after typical LCP measurement window
    
    let timeoutId: ReturnType<typeof setTimeout>;
    
    // Use requestIdleCallback if available for even better scheduling
    if ('requestIdleCallback' in window) {
      const idleCallbackId = (window as any).requestIdleCallback(() => {
        timeoutId = setTimeout(() => {
          setIsLoading(true);
          fetchExchangeRates();
        }, delayMs);
      }, { timeout: 10000 });
      
      return () => {
        (window as any).cancelIdleCallback(idleCallbackId);
        if (timeoutId) clearTimeout(timeoutId);
      };
    } else {
      // Fallback for browsers without requestIdleCallback
      timeoutId = setTimeout(() => {
        setIsLoading(true);
        fetchExchangeRates();
      }, delayMs);
      
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
  };

  const convertPrice = (priceInEur: number): number => {
    if (currency === 'EUR') return priceInEur;
    if (currency === 'USD') return Math.round(priceInEur * exchangeRates.USD);
    if (currency === 'TRY') return Math.round(priceInEur * exchangeRates.TRY);
    return priceInEur;
  };

  const formatPrice = (priceInEur: number): string => {
    const converted = convertPrice(priceInEur);
    let symbol = '€';
    if (currency === 'USD') symbol = '$';
    if (currency === 'TRY') symbol = '₺';
    return `${symbol}${converted.toLocaleString()}`;
  };

  const getCurrencySymbol = (): string => {
    if (currency === 'EUR') return '€';
    if (currency === 'USD') return '$';
    if (currency === 'TRY') return '₺';
    return '€';
  };

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      exchangeRates,
      convertPrice,
      formatPrice,
      currencySymbol: getCurrencySymbol(),
      isLoading,
      exchangeRate: exchangeRates.USD
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
